// Public ticket-flow intake page (/flow/:slug) — NO login required. Renders a
// public-enabled flow's intake form for an anonymous end-user with the flow's
// custom branding (logo + brand colour), collects the requester's name/email,
// and initiates the flow via the public endpoint. Lives OUTSIDE the authed
// Layout/RequireAuth in App.tsx (mirrors PublicFormPage).

import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { ApiError, getPublicTicketFlow, initiatePublicTicketFlow } from "../lib/api";
import { Alert, Loading, Spinner } from "../components/States";
import { Logo } from "../components/Logo";
import type { PublicTicketFlow, TicketFlowField } from "../lib/types";

export function PublicFlowPage() {
  const { slug = "" } = useParams();
  const [flow, setFlow] = useState<PublicTicketFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [values, setValues] = useState<Record<string, unknown>>({});
  const setValue = (key: string, v: unknown) => setValues((prev) => ({ ...prev, [key]: v }));
  const [missing, setMissing] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getPublicTicketFlow(slug)
      .then((res) => {
        if (cancelled) return;
        setFlow(res.flow);
        const init: Record<string, unknown> = {};
        for (const f of res.flow.intake_form) init[f.key] = f.type === "checkbox" ? false : "";
        setValues(init);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof ApiError && e.status === 404
            ? "This flow isn't available. It may be disabled or the link is incorrect."
            : e instanceof Error
              ? e.message
              : "Could not load this flow.",
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!flow || submitting) return;
    const unmet = flow.intake_form
      .filter((f) => {
        if (!f.required) return false;
        const v = values[f.key];
        return f.type === "checkbox" ? v !== true : String(v ?? "").trim() === "";
      })
      .map((f) => f.key);
    setMissing(unmet);
    if (unmet.length > 0 || !requesterEmail.trim().includes("@")) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await initiatePublicTicketFlow(slug, {
        form_data: values,
        requester: {
          ...(requesterName.trim() ? { name: requesterName.trim() } : {}),
          email: requesterEmail.trim(),
        },
      });
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Custom brand colour recolours the CTA (--brand-primary) and accents (--brand).
  const brandStyle: CSSProperties = flow?.brand_color
    ? ({ "--brand-primary": flow.brand_color, "--brand": flow.brand_color } as CSSProperties)
    : {};

  function renderField(f: TicketFlowField) {
    const invalid = missing.includes(f.key);
    if (f.type === "checkbox") {
      return (
        <label key={f.key} className="toggle">
          <input
            type="checkbox"
            checked={values[f.key] === true}
            onChange={(e) => setValue(f.key, e.target.checked)}
          />
          {f.label}
          {f.required ? <span style={{ color: "var(--danger, #dc2626)" }}> *</span> : null}
        </label>
      );
    }
    return (
      <label key={f.key} className="field">
        <span className="label">
          {f.label}
          {f.required ? <span style={{ color: "var(--danger, #dc2626)" }}> *</span> : null}
        </span>
        {f.type === "textarea" ? (
          <textarea rows={4} value={String(values[f.key] ?? "")} onChange={(e) => setValue(f.key, e.target.value)} />
        ) : f.type === "select" ? (
          <select value={String(values[f.key] ?? "")} onChange={(e) => setValue(f.key, e.target.value)}>
            <option value="">— select —</option>
            {(f.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={f.type === "email" ? "email" : f.type === "date" ? "date" : "text"}
            value={String(values[f.key] ?? "")}
            onChange={(e) => setValue(f.key, e.target.value)}
          />
        )}
        {invalid ? (
          <span className="hint" style={{ color: "var(--danger, #dc2626)" }}>
            This field is required.
          </span>
        ) : null}
      </label>
    );
  }

  return (
    <div className="public-form-shell" style={brandStyle}>
      <div className="public-form-card">
        <div className="public-form-brand">
          {flow?.brand_logo_url ? (
            <img src={flow.brand_logo_url} alt={flow.name} className="public-form-logo" />
          ) : (
            <Logo size={26} />
          )}
        </div>

        {loading ? (
          <Loading label="Loading…" />
        ) : loadError ? (
          <Alert kind="error">{loadError}</Alert>
        ) : done ? (
          <div className="public-form-done">
            <div className="public-form-check" aria-hidden="true">
              ✓
            </div>
            <h1>Thank you!</h1>
            <p className="muted">“{flow?.name}” has been submitted. We'll follow up by email.</p>
          </div>
        ) : flow ? (
          <>
            <h1 className="public-form-title">{flow.name}</h1>
            {flow.description ? <p className="public-form-desc">{flow.description}</p> : null}
            <form onSubmit={onSubmit} className="form-preview">
              {submitError ? <Alert kind="error">{submitError}</Alert> : null}
              <label className="field">
                <span className="label">Your name</span>
                <input type="text" value={requesterName} onChange={(e) => setRequesterName(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">
                  Your email<span style={{ color: "var(--danger, #dc2626)" }}> *</span>
                </span>
                <input
                  type="email"
                  value={requesterEmail}
                  onChange={(e) => setRequesterEmail(e.target.value)}
                  required
                />
              </label>
              {flow.intake_form.map(renderField)}
              <button className="btn primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner label="Submitting…" /> : "Submit"}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}
